import React from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Shield, Eye, Cookie, Database, Mail, Clock } from "lucide-react";
import {
  BRAND_OPERATOR_LINE,
  COMPANY,
  formatCompanyMailingAddress,
} from "@/lib/companyInfo";

const Privacy = () => {
  useSEO({
    title: "Privacy Policy - Female Partner Calculator | Data & Cookie Practices",
    description:
      "How Female Partner Calculator (operated by Summit Lane Digital, LLC) handles analytics, cookies, and any information collected through the site.",
    canonical: "https://femalepartnercalculator.com/privacy",
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
            </div>
            <p className="text-lg text-muted-foreground">
              This policy explains what information {COMPANY.brandName} collects and how it is used.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Last updated: July 2026</span>
            </div>
            <p className="text-sm text-muted-foreground">{BRAND_OPERATOR_LINE}</p>
          </div>

          {/* The Calculator Itself */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                The Calculator Runs on Your Device
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                All calculator inputs (age range, height, income, education, and other preferences you
                select) are processed entirely in your browser using publicly published demographic
                statistics. Your selections are never transmitted to, stored on, or seen by our servers.
                We do not know what criteria you entered.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Information We Do Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Automatically Collected (Analytics)</h3>
                <p className="text-muted-foreground mb-2">
                  If you accept cookies via the consent banner, we use Google Analytics (GA4) and Microsoft
                  Clarity to understand aggregate usage. These may collect:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>IP address (used by the provider to derive approximate location, then discarded)</li>
                  <li>Browser type, device type, and screen size</li>
                  <li>Pages visited, time on page, and referral source</li>
                  <li>Anonymized interaction data (clicks, scrolling) via Microsoft Clarity session recordings</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Information You Choose to Provide</h3>
                <p className="text-muted-foreground">
                  If a newsletter signup form is active on the site, we collect the email address you submit
                  so we can send the requested content. We do not sell or rent email addresses. If no
                  newsletter is configured at the time you visit, no signup form is shown and no email is
                  collected through it.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Donations</h3>
                <p className="text-muted-foreground">
                  Optional donations are processed entirely by PayPal on PayPal&apos;s own site. We never
                  receive or store your payment card or bank details &mdash; see PayPal&apos;s privacy policy
                  for how it handles that information.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cookies & Consent */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" />
                Cookies & Consent
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                On your first visit, a banner asks whether you consent to analytics cookies. Analytics and
                advertising storage default to <strong>denied</strong> (Google Consent Mode v2) until you
                click &quot;Accept.&quot; If you click &quot;Decline,&quot; Google Analytics and Microsoft
                Clarity are not loaded and no analytics cookies are set. Your choice is remembered in your
                browser&apos;s local storage so you are not asked again on the same device.
              </p>
              <p className="text-muted-foreground">
                You can change your mind at any time by clearing your browser&apos;s local storage for this
                site, which will show the banner again on your next visit.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate & Sponsored Links */}
          <Card>
            <CardHeader>
              <CardTitle>Affiliate & Sponsored Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Some results pages may show a sponsored link to a third-party dating service, clearly marked
                as a disclosure. If you click it, that third party may set its own cookies and collect
                information under its own privacy policy &mdash; {COMPANY.brandName} does not control or
                receive any personal data from that click beyond an anonymous referral credit.
              </p>
            </CardContent>
          </Card>

          {/* Non-Affiliation */}
          <Card>
            <CardHeader>
              <CardTitle>Data Sources & Non-Affiliation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                The statistics used in the calculator are drawn from publicly published data from the U.S.
                Census Bureau, CDC NHANES, and Pew Research Center. Referencing this data is for attribution
                only; {COMPANY.brandName} is not affiliated with, endorsed by, or sponsored by any government
                agency or research institution.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle>Your Privacy Rights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Depending on your location, you may have the right to request access to, correction of, or
                deletion of any personal data we hold about you (for example, an email address you submitted
                to a newsletter form). Because the calculator itself does not transmit or store your inputs,
                there is generally nothing to request in connection with calculator use itself.
              </p>
              <p className="text-muted-foreground">
                To exercise these rights, or to opt out of analytics, contact us using the details below or
                decline cookies via the consent banner.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>Children&apos;s Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {COMPANY.brandName} is not directed at children under 13, and we do not knowingly collect
                personal information from children.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                If you have questions about this privacy policy or want to exercise your privacy rights,
                contact us:
              </p>
              <div className="space-y-2">
                <p>
                  <strong>Data controller:</strong> {COMPANY.legalName}
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  <a
                    href={`mailto:${COMPANY.emails.privacy}`}
                    className="text-primary hover:underline"
                  >
                    {COMPANY.emails.privacy}
                  </a>
                </p>
                <p>
                  <strong>Address:</strong> {formatCompanyMailingAddress()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Updates */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Updates</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We may update this privacy policy periodically to reflect changes in our practices or legal
                requirements. Material changes will be reflected by an updated &quot;Last updated&quot; date
                on this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
